import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Siren, AlertTriangle, Radar } from 'lucide-react';
import { BRAND } from '../lib/brand';
import { openWhatsApp } from '../lib/whatsapp';

/**
 * Mobile-only sticky bottom tab bar - dark glass pill with monochrome
 * line icons + a raised red FAB in the centre. Matches the user's
 * reference design (dark navy glass, white outline icons, green
 * brand-coloured WhatsApp, large red Unfall melden circle with soft
 * red glow). Hidden on /admin/** and the immersive /notfall page.
 */

// Routes where the floating bottom-nav must stay out of the way because
// the page itself owns a fixed action bar (e.g. "Weiter" in the wizard)
// or runs in an immersive full-screen mode.
const HIDDEN_PREFIXES = ['/admin', '/notfall', '/unfall-melden'];

/* Inline WhatsApp logo so the bar carries proper brand recognition
   (lucide doesn't ship an official WhatsApp mark). */
function WhatsAppGlyph({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.01ZM12.05 20.15h-.01a8.23 8.23 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.26 8.26 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.82 2.41a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.7 8.23-8.23 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.7-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.41-.56-.42l-.48-.01a.92.92 0 0 0-.67.31c-.23.25-.88.86-.88 2.09 0 1.23.9 2.42 1.03 2.59.12.16 1.78 2.72 4.31 3.81.6.26 1.07.41 1.43.53.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.1-.23-.16-.48-.28Z"/>
    </svg>
  );
}

export default function MobileBottomNav() {
  const location = useLocation();
  const path = location.pathname || '/';

  if (HIDDEN_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))) {
    return null;
  }

  const isUnfallActive = path.startsWith('/unfall-melden');
  const isTrackActive = path.startsWith('/track');
  const isNotfallActive = path === '/notfall';

  const onWhatsApp = (e) => {
    e.preventDefault();
    openWhatsApp('Hallo UnfallHeld, ich brauche Unterstützung nach einem Unfall.');
  };

  return (
    <>
      {/* spacer keeps page content above the floating nav */}
      <div className="md:hidden h-24" aria-hidden="true" />

      <nav
        className="fixed inset-x-3 bottom-3 z-40 md:hidden"
        aria-label="Schnellzugriff"
        data-testid="mobile-bottom-nav"
        style={{ marginBottom: 'max(0px, env(safe-area-inset-bottom))' }}
      >
        <div className="relative">
          {/* soft red halo radiating from the FAB to give the bar warmth */}
          <div
            className="absolute -inset-x-2 -bottom-4 h-16 bg-red-600/25 blur-3xl rounded-full pointer-events-none"
            aria-hidden="true"
          />

          {/* Dark glass pill */}
          <div
            className="relative rounded-[28px] border border-white/10 shadow-[0_18px_60px_-12px_rgba(0,0,0,0.55)] overflow-visible"
            style={{
              background:
                'linear-gradient(180deg, rgba(15,23,42,0.86) 0%, rgba(10,15,30,0.92) 100%)',
              backdropFilter: 'blur(28px) saturate(140%)',
              WebkitBackdropFilter: 'blur(28px) saturate(140%)',
            }}
          >
            {/* subtle inner highlight along the top edge (glass effect) */}
            <div
              className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent rounded-full pointer-events-none"
              aria-hidden="true"
            />

            <div className="relative grid grid-cols-5 items-end px-2 pt-2 pb-2.5">
              {/* Slot 1 - Anrufen */}
              <a
                href={`tel:${BRAND.phone}`}
                className="group flex flex-col items-center justify-center gap-1 py-1.5 rounded-2xl active:scale-90 transition-transform"
                data-testid="bottom-nav-call"
                aria-label="UnfallHeld anrufen"
              >
                <Phone
                  className="h-[26px] w-[26px] text-white/95 transition-transform group-hover:scale-110"
                  strokeWidth={1.75}
                />
                <span className="text-[10.5px] font-medium leading-none text-white/85">Anruf</span>
              </a>

              {/* Slot 2 - WhatsApp */}
              <button
                type="button"
                onClick={onWhatsApp}
                className="group flex flex-col items-center justify-center gap-1 py-1.5 rounded-2xl active:scale-90 transition-transform"
                data-testid="bottom-nav-whatsapp"
                aria-label="WhatsApp Kontakt"
              >
                <WhatsAppGlyph className="h-[26px] w-[26px] text-[#25D366] transition-transform group-hover:scale-110" />
                <span className="text-[10.5px] font-medium leading-none text-white/85">WhatsApp</span>
              </button>

              {/* Slot 3 - CENTER FAB: Unfall melden (raised, red, glowing) */}
              <div className="flex flex-col items-center justify-end -mt-7">
                <Link
                  to="/unfall-melden"
                  className="relative inline-flex h-[60px] w-[60px] items-center justify-center rounded-full text-white active:scale-95 transition-transform"
                  style={{
                    background:
                      'radial-gradient(circle at 32% 28%, #f87171 0%, #ef4444 45%, #b91c1c 100%)',
                    boxShadow:
                      '0 0 0 4px rgba(15,23,42,0.92), 0 14px 36px -6px rgba(220,38,38,0.75), inset 0 1px 0 rgba(255,255,255,0.35)',
                  }}
                  data-testid="bottom-nav-report"
                  aria-label="Unfall melden"
                >
                  {/* pulsing aura while not already on the wizard */}
                  {!isUnfallActive && (
                    <>
                      <span
                        className="absolute inset-0 rounded-full bg-red-500/45 animate-ping pointer-events-none"
                        aria-hidden="true"
                      />
                      <span
                        className="absolute -inset-1.5 rounded-full bg-red-500/25 animate-pulse pointer-events-none"
                        aria-hidden="true"
                      />
                    </>
                  )}
                  <Siren className="relative h-7 w-7" strokeWidth={2} fill="currentColor" />
                </Link>
                <span className="mt-1 text-[10.5px] font-bold leading-none text-white tracking-tight">
                  Melden
                </span>
              </div>

              {/* Slot 4 - Notfall */}
              <Link
                to="/notfall"
                className={`group flex flex-col items-center justify-center gap-1 py-1.5 rounded-2xl active:scale-90 transition-transform ${
                  isNotfallActive ? 'text-amber-300' : 'text-white/95'
                }`}
                data-testid="bottom-nav-emergency"
                aria-label="Notfallmodus öffnen"
              >
                <AlertTriangle
                  className={`h-[26px] w-[26px] transition-transform group-hover:scale-110 ${
                    isNotfallActive ? 'text-amber-300' : 'text-white/95'
                  }`}
                  strokeWidth={1.75}
                />
                <span className="text-[10.5px] font-medium leading-none text-white/85">Notfall</span>
              </Link>

              {/* Slot 5 - Status */}
              <Link
                to="/track"
                className={`group flex flex-col items-center justify-center gap-1 py-1.5 rounded-2xl active:scale-90 transition-transform ${
                  isTrackActive ? 'text-[#9FC3FF]' : 'text-white/95'
                }`}
                data-testid="bottom-nav-status"
                aria-label="Fall-Status verfolgen"
              >
                <Radar
                  className={`h-[26px] w-[26px] transition-transform group-hover:scale-110 ${
                    isTrackActive ? 'text-[#9FC3FF]' : 'text-white/95'
                  }`}
                  strokeWidth={1.75}
                />
                <span className="text-[10.5px] font-medium leading-none text-white/85">Status</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
